import { StickyTableHeader } from 'vh-sticky-table-header';
import React, { forwardRef, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export const TableWithStickyHeader = forwardRef(({ children, isPrint }, ref) => {
  const tableRef = useRef(null);
  const tableCloneRef = useRef(null);
  const cloneWrapRef = useRef(null);

  useLayoutEffect(() => {
    if (tableRef.current && tableCloneRef.current) {
      // Offset the pinned header below the app's sticky 80px header bar
      // (`.header-wrapper`). Inside a `.preview-modal` the modal is a fixed
      // overlay that covers that bar, so there the header should pin to the
      // very top (offset 0).
      const inModal = !!tableRef.current.closest('.preview-modal');
      const headerBar = document.querySelector('.header-wrapper');
      const stickOffset = isPrint || inModal ? 0 : headerBar?.offsetHeight ?? 0;

      // Initialize the sticky header.
      const sticky = new StickyTableHeader(tableRef.current, tableCloneRef.current, {
        max: stickOffset,
      });

      // The form is rendered inside `.render-form-scale-wrapper`, which has a
      // `transform: scale(...)`. A transformed ancestor becomes the containing
      // block for `position: fixed` descendants, so the library's fixed clone
      // gets pinned relative to the scaled wrapper (next to the main header)
      // instead of the viewport. We render the clone through a portal into
      // <body> so it escapes the transform and `position: fixed` resolves
      // against the viewport again. The library positions the clone but never
      // sets `left`, so we sync that to the live table position ourselves.
      const syncLeft = () => {
        const wrap = cloneWrapRef.current;
        const container = tableRef.current?.parentElement;
        if (wrap && container) {
          wrap.style.left = `${container.getBoundingClientRect().left}px`;
        }
      };

      // The library only listens for scroll on `window` and whatever scroll
      // containers existed at construction time. In this app the form scrolls
      // inside nested containers (e.g. `.preview-modal`, `.send-form-wrapper`),
      // so catch scroll from *any* element via a capture-phase listener and
      // re-trigger the library's recompute. The library repositions from
      // absolute getBoundingClientRect values, so forwarding the event is enough.
      const onAnyScroll = (event) => {
        if (event.target !== window && event.target !== document) {
          window.dispatchEvent(new Event('scroll'));
        }
        syncLeft();
      };
      document.addEventListener('scroll', onAnyScroll, { capture: true, passive: true });
      window.addEventListener('scroll', syncLeft, { passive: true });
      window.addEventListener('resize', syncLeft);
      syncLeft();

      // Make sure to destory the sticky header once the main table is unmounted.
      return () => {
        document.removeEventListener('scroll', onAnyScroll, { capture: true });
        window.removeEventListener('scroll', syncLeft);
        window.removeEventListener('resize', syncLeft);
        sticky.destroy();
      };
    }
    // eslint-disable-next-line
  }, [children]);

  return (
    <div style={{ maxWidth: '100%' }}>
      <div className="table_container" style={{ minWidth: 'unset' }} ref={ref}>
        <table className={'form-control-table'} ref={tableRef}>
          {children}
        </table>
      </div>
      {createPortal(
        <div className="table_container sticky-table-clone" ref={cloneWrapRef} style={{ display: 'none', zIndex: 100000 }}>
          <table className={'form-control-table'} ref={tableCloneRef} />
        </div>,
        document.body,
      )}
    </div>
  );
});
